// registerWizardPrimitives — augments the canonical @fields/registry at
// module-load time with the 2 pending-primitive types used by CHG-1241
// manifests (text, checkbox), so FormDialog wizard mode can resolve them.
//
// Mirrors the v1 SpecWizard MIGRATION_PLAYBOOK pattern referenced in the
// import-map manifest's forward_promises: "Registered at runtime via
// registerWizardComponent per v1 SpecWizard MIGRATION_PLAYBOOK convention".
//
// This is the SOURCE of those two entries — until the field-primitive
// registry expansion CHG ships them natively. When that lands, this
// registration becomes idempotent (??= guards) and can eventually be removed.
//
// The fields/ source itself is unchanged (immutable per brief); we mutate
// the Record at runtime, which is the documented v1 extension pattern.

import type { ComponentType } from "react";
import { fieldRegistry } from "@fields/registry";
import { WizardPendingTextField, WizardPendingCheckboxField } from "./WizardPendingField";
import { registerWizardComponent } from "./customRenderRegistry";
// CHG-1260 — point at the REAL implementations under ./components/.
// The CHG-1247 stubs at ./customRenders/Import{ColumnMappingTable,PreviewTable}.tsx
// are now orphaned-but-immutable (per "CHG-1247 immutable" constraint);
// a future cleanup CHG removes them.
import { ImportColumnMappingTable } from "./components/ImportColumnMappingTable";
import { ImportPreviewTable } from "./components/ImportPreviewTable";

let _registered = false;

/** Idempotent — safe to call from multiple wizard entry-points. */
export function registerWizardPrimitives(): void {
  if (_registered) return;
  _registered = true;

  // Add the 2 pending-primitive types used across the 5 manifests. ??= so
  // the future field-primitive expansion CHG's native primitives take
  // precedence if it lands first.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const reg = fieldRegistry as Record<string, ComponentType<any>>;
  reg.text ??= WizardPendingTextField;
  reg.checkbox ??= WizardPendingCheckboxField;

  // CHG-1260 — register the REAL implementations of the 2 forward-promised
  // custom-render components from CHG-1241. CHG-1247 originally registered
  // stubs here; this registration replaces those stubs with the real
  // components under ./components/. No more data-wizard-stub="true" output
  // for these 2 IDs.
  registerWizardComponent("ImportColumnMappingTable", ImportColumnMappingTable);
  registerWizardComponent("ImportPreviewTable", ImportPreviewTable);
}

/** Test helper — reset for isolation. Production code never calls this. */
export function _resetWizardPrimitivesForTests(): void {
  _registered = false;
}
